import React, { useState, useCallback, useRef } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download, Play, Loader } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

type Step = 'upload' | 'analyze' | 'mapping' | 'preview' | 'execute' | 'done';

interface ColumnInfo {
  name: string;
  index: number;
  detected_type: string;
  sample_values: string[];
  null_count: number;
  unique_count: number;
}

interface Mapping {
  source_field: string;
  target_field: string;
  transform?: { type: string };
  required: boolean;
  confidence?: number;
}

interface PreviewRow {
  row_number: number;
  original: Record<string, string>;
  transformed: Record<string, unknown>;
  valid: boolean;
  errors: string[];
}

const PRODUCTS = [
  { id: 'psa', name: 'PSA', entities: ['ticket', 'contact', 'company', 'time_entry'] },
  { id: 'crm', name: 'CRM', entities: ['contact', 'company'] },
  { id: 'cmdb', name: 'CMDB', entities: ['configuration_item', 'flexible_asset', 'password'] },
  { id: 'books', name: 'Books', entities: ['customer', 'invoice'] },
];

export default function ImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [targetProduct, setTargetProduct] = useState('psa');
  const [targetEntity, setTargetEntity] = useState('contact');
  const [fileId, setFileId] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Analysis results
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  // Preview results
  const [transformedPreview, setTransformedPreview] = useState<PreviewRow[]>([]);
  const [previewSummary, setPreviewSummary] = useState({ total: 0, valid: 0, errors: 0 });

  // Execution results
  const [jobStatus, setJobStatus] = useState<{ status: string; processed: number; succeeded: number; failed: number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    setError('');
    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_product', targetProduct);
    formData.append('target_entity', targetEntity);

    try {
      const result = await api.upload<{ file_id: string; file_name: string; file_size: number }>('/import/upload', formData);
      setFileId(result.file_id);
      setFileName(result.file_name);
      setFileSize(result.file_size);
      setStep('analyze');

      // Auto-analyze
      const analysis = await api.post<{
        columns: ColumnInfo[];
        suggested_mappings: Mapping[];
        preview_rows: Record<string, string>[];
        total_rows: number;
      }>('/import/analyze', { file_id: result.file_id });

      setColumns(analysis.columns);
      setMappings(analysis.suggested_mappings);
      setPreviewRows(analysis.preview_rows);
      setTotalRows(analysis.total_rows);
      setStep('mapping');
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  }, [targetProduct, targetEntity]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.post<{
        preview_rows: PreviewRow[];
        summary: { total: number; valid: number; errors: number };
      }>('/import/preview', {
        file_id: fileId,
        mappings: mappings.map(m => ({
          source_field: m.source_field,
          target_field: m.target_field,
          transform: m.transform || { type: 'direct' },
          required: m.required,
        })),
      });
      setTransformedPreview(result.preview_rows);
      setPreviewSummary(result.summary);
      setStep('preview');
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleExecute = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.post<{
        job_id: string;
        status: string;
        progress: { processed: number; succeeded: number; failed: number };
      }>('/import/execute', { file_id: fileId });

      setJobStatus({ status: result.status, ...result.progress });
      setStep('done');
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const updateMapping = (sourceField: string, targetField: string) => {
    setMappings(prev => prev.map(m =>
      m.source_field === sourceField ? { ...m, target_field: targetField } : m
    ));
  };

  const product = PRODUCTS.find(p => p.id === targetProduct);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#111827' }}>CSV Import</h2>
        <a href="/api/templates" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: ACCENT, textDecoration: 'none' }}>
          <Download size={14} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
          Download Templates
        </a>
      </div>

      {/* Target Selection */}
      {step === 'upload' && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.375rem', fontSize: '0.875rem' }}>Target Product</label>
            <select value={targetProduct} onChange={e => { setTargetProduct(e.target.value); setTargetEntity(PRODUCTS.find(p => p.id === e.target.value)?.entities[0] || ''); }} style={selectStyle}>
              {PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.375rem', fontSize: '0.875rem' }}>Entity Type</label>
            <select value={targetEntity} onChange={e => setTargetEntity(e.target.value)} style={selectStyle}>
              {product?.entities.map(e => <option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}s</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Upload Zone */}
      {step === 'upload' && (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? ACCENT : '#d1d5db'}`,
            borderRadius: '0.75rem',
            padding: '3rem',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragOver ? '#fff7ed' : '#fff',
            transition: 'all 0.15s',
          }}
        >
          <Upload size={48} color={dragOver ? ACCENT : '#9ca3af'} style={{ marginBottom: '1rem' }} />
          <p style={{ fontWeight: 600, fontSize: '1rem', color: '#374151' }}>Drag & drop your CSV file here</p>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>or click to browse (max 50MB)</p>
          <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} style={{ display: 'none' }} />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '0.75rem' }}>
          <Loader size={20} color={ACCENT} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: '#6b7280' }}>Processing...</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', color: '#dc2626', fontSize: '0.875rem', marginTop: '1rem' }}>
          {error}
        </div>
      )}

      {/* Column Analysis & Mapping */}
      {step === 'mapping' && !loading && (
        <div>
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                  <FileText size={16} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  {fileName}
                </h3>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  {totalRows.toLocaleString()} rows &middot; {columns.length} columns &middot; {(fileSize / 1024).toFixed(1)} KB
                </p>
              </div>
              <span style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', backgroundColor: '#f0fdf4', color: '#16a34a', borderRadius: '0.25rem' }}>Analyzed</span>
            </div>

            {/* Auto-detected columns */}
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem' }}>Detected Columns</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>CSV Column</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Samples</th>
                  <th style={thStyle}>Nulls</th>
                </tr>
              </thead>
              <tbody>
                {columns.map(col => (
                  <tr key={col.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{col.name}</td>
                    <td style={tdStyle}><TypeBadge type={col.detected_type} /></td>
                    <td style={{ ...tdStyle, color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.sample_values.join(', ')}</td>
                    <td style={tdStyle}>{col.null_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mapping UI */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Column Mapping</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>Source Column</th>
                  <th style={thStyle}></th>
                  <th style={thStyle}>Target Field</th>
                  <th style={thStyle}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m.source_field} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{m.source_field}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>&rarr;</td>
                    <td style={tdStyle}>
                      <select value={m.target_field} onChange={e => updateMapping(m.source_field, e.target.value)} style={{ ...selectStyle, width: '100%', padding: '0.25rem 0.5rem' }}>
                        <option value="">-- Skip --</option>
                        {product?.entities.includes(targetEntity) && getTargetFields(targetProduct, targetEntity).map(f => (
                          <option key={f.field} value={f.field}>{f.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      {m.confidence !== undefined && (
                        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: m.confidence >= 90 ? '#16a34a' : m.confidence >= 70 ? '#ca8a04' : '#6b7280' }}>
                          {m.confidence}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Preview rows */}
          {previewRows.length > 0 && (
            <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem', overflow: 'auto' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Preview (first {Math.min(10, previewRows.length)} rows)</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    {columns.map(col => <th key={col.name} style={thStyle}>{col.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.slice(0, 10).map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      {columns.map(col => <td key={col.name} style={tdStyle}>{row[col.name] || ''}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button onClick={handlePreview} disabled={mappings.length === 0} style={primaryBtnStyle}>
            Preview Transformation
          </button>
        </div>
      )}

      {/* Preview Results */}
      {step === 'preview' && !loading && (
        <div>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <StatCard icon={CheckCircle} color="#10b981" label="Valid" value={previewSummary.valid} />
            <StatCard icon={XCircle} color="#ef4444" label="Errors" value={previewSummary.errors} />
            <StatCard icon={FileText} color="#6b7280" label="Total Rows" value={totalRows} />
          </div>

          <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem', overflow: 'auto' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Transformed Preview</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Status</th>
                  {mappings.map(m => <th key={m.target_field} style={thStyle}>{m.target_field}</th>)}
                  <th style={thStyle}>Errors</th>
                </tr>
              </thead>
              <tbody>
                {transformedPreview.map(row => (
                  <tr key={row.row_number} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: row.valid ? 'transparent' : '#fef2f2' }}>
                    <td style={tdStyle}>{row.row_number}</td>
                    <td style={tdStyle}>{row.valid ? <CheckCircle size={14} color="#10b981" /> : <XCircle size={14} color="#ef4444" />}</td>
                    {mappings.map(m => <td key={m.target_field} style={tdStyle}>{String(row.transformed[m.target_field] ?? '')}</td>)}
                    <td style={{ ...tdStyle, color: '#ef4444' }}>{row.errors.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => setStep('mapping')} style={{ ...secondaryBtnStyle }}>Back to Mapping</button>
            <button onClick={handleExecute} style={primaryBtnStyle}>
              <Play size={16} /> Execute Import ({totalRows.toLocaleString()} rows)
            </button>
          </div>
        </div>
      )}

      {/* Done */}
      {step === 'done' && jobStatus && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '2rem', textAlign: 'center' }}>
          <CheckCircle size={48} color="#10b981" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.5rem' }}>Import {jobStatus.status === 'completed' ? 'Complete' : 'Finished'}</h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', margin: '1rem 0' }}>
            <div><span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{jobStatus.succeeded}</span><br /><span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Succeeded</span></div>
            <div><span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{jobStatus.failed}</span><br /><span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Failed</span></div>
            <div><span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#6b7280' }}>{jobStatus.processed}</span><br /><span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Processed</span></div>
          </div>
          {jobStatus.failed > 0 && (
            <button onClick={() => window.open(`/api/import/${fileId}/errors`, '_blank')} style={{ ...secondaryBtnStyle, display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <Download size={16} /> Download Error Report
            </button>
          )}
          <div style={{ marginTop: '1rem' }}>
            <button onClick={() => { setStep('upload'); setFileId(''); setFileName(''); setColumns([]); setMappings([]); setPreviewRows([]); setTransformedPreview([]); setJobStatus(null); setError(''); }} style={primaryBtnStyle}>
              Import Another File
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function TypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    email: '#8b5cf6', phone: '#06b6d4', date: '#f59e0b', number: '#3b82f6',
    boolean: '#10b981', url: '#ec4899', string: '#6b7280',
  };
  return (
    <span style={{ fontSize: '0.7rem', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', backgroundColor: `${colors[type] || '#6b7280'}15`, color: colors[type] || '#6b7280', fontWeight: 500 }}>
      {type}
    </span>
  );
}

function StatCard({ icon: Icon, color, label, value }: { icon: any; color: string; label: string; value: number }) {
  return (
    <div style={{ flex: 1, backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <Icon size={24} color={color} />
      <div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{value.toLocaleString()}</div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</div>
      </div>
    </div>
  );
}

function getTargetFields(product: string, entity: string): { field: string; label: string }[] {
  const schemas: Record<string, Record<string, { field: string; label: string }[]>> = {
    psa: {
      ticket: [{ field: 'subject', label: 'Subject' }, { field: 'description', label: 'Description' }, { field: 'status', label: 'Status' }, { field: 'priority', label: 'Priority' }, { field: 'company_id', label: 'Company ID' }, { field: 'contact_id', label: 'Contact ID' }, { field: 'assigned_to', label: 'Assigned To' }, { field: 'due_date', label: 'Due Date' }, { field: 'created_at', label: 'Created Date' }],
      contact: [{ field: 'first_name', label: 'First Name' }, { field: 'last_name', label: 'Last Name' }, { field: 'email', label: 'Email' }, { field: 'phone', label: 'Phone' }, { field: 'mobile', label: 'Mobile' }, { field: 'title', label: 'Title' }, { field: 'company_id', label: 'Company ID' }],
      company: [{ field: 'name', label: 'Company Name' }, { field: 'phone', label: 'Phone' }, { field: 'website', label: 'Website' }, { field: 'address', label: 'Address' }, { field: 'city', label: 'City' }, { field: 'state', label: 'State' }, { field: 'zip', label: 'ZIP' }, { field: 'type', label: 'Type' }],
      time_entry: [{ field: 'ticket_id', label: 'Ticket ID' }, { field: 'user_id', label: 'User ID' }, { field: 'hours', label: 'Hours' }, { field: 'notes', label: 'Notes' }, { field: 'date', label: 'Date' }, { field: 'billable', label: 'Billable' }],
    },
    crm: {
      contact: [{ field: 'first_name', label: 'First Name' }, { field: 'last_name', label: 'Last Name' }, { field: 'email', label: 'Email' }, { field: 'phone', label: 'Phone' }, { field: 'company_id', label: 'Company ID' }, { field: 'source', label: 'Source' }, { field: 'status', label: 'Status' }],
      company: [{ field: 'name', label: 'Company Name' }, { field: 'industry', label: 'Industry' }, { field: 'website', label: 'Website' }, { field: 'phone', label: 'Phone' }, { field: 'size', label: 'Size' }],
    },
    cmdb: {
      configuration_item: [{ field: 'name', label: 'Name' }, { field: 'ci_type', label: 'CI Type' }, { field: 'company_id', label: 'Company ID' }, { field: 'serial_number', label: 'Serial Number' }, { field: 'manufacturer', label: 'Manufacturer' }, { field: 'model', label: 'Model' }, { field: 'status', label: 'Status' }, { field: 'ip_address', label: 'IP Address' }, { field: 'location', label: 'Location' }],
      flexible_asset: [{ field: 'name', label: 'Name' }, { field: 'asset_type', label: 'Asset Type' }, { field: 'company_id', label: 'Company ID' }, { field: 'fields', label: 'Fields (JSON)' }],
      password: [{ field: 'name', label: 'Name' }, { field: 'username', label: 'Username' }, { field: 'password_value', label: 'Password' }, { field: 'url', label: 'URL' }, { field: 'company_id', label: 'Company ID' }, { field: 'notes', label: 'Notes' }],
    },
    books: {
      customer: [{ field: 'name', label: 'Customer Name' }, { field: 'email', label: 'Email' }, { field: 'phone', label: 'Phone' }, { field: 'address', label: 'Address' }, { field: 'city', label: 'City' }, { field: 'state', label: 'State' }, { field: 'zip', label: 'ZIP' }, { field: 'tax_id', label: 'Tax ID' }],
      invoice: [{ field: 'customer_id', label: 'Customer ID' }, { field: 'invoice_number', label: 'Invoice Number' }, { field: 'date', label: 'Invoice Date' }, { field: 'due_date', label: 'Due Date' }, { field: 'total', label: 'Total' }, { field: 'status', label: 'Status' }, { field: 'notes', label: 'Notes' }],
    },
  };
  return schemas[product]?.[entity] || [];
}

const selectStyle: React.CSSProperties = { padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.875rem', backgroundColor: '#fff' };
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '0.5rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' };
const tdStyle: React.CSSProperties = { padding: '0.5rem 0.75rem', fontSize: '0.8125rem' };
const primaryBtnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.25rem', backgroundColor: ACCENT, color: '#fff', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' };
const secondaryBtnStyle: React.CSSProperties = { padding: '0.5rem 1.25rem', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' };
